import { EventUpdateRequest } from '@/common/types';

const MAX_CALL_LIMIT = 20;

const keyAliases = new Map<string, string>();

const purgeOnly = (key: string) => purgeByKey(key);

const normalizeKey = (key?: string) => {
  if (!key) return key;

  return keyAliases.get(key) ?? key;
};

const setQueueKeyAlias = (fromKey: string, toKey: string) => {
  keyAliases.set(fromKey, toKey);

  const from = normalizeKey(fromKey) ?? fromKey;
  const to = normalizeKey(toKey) ?? toKey;

  for (let i = 0; i < requestQueue.length; i++) {
    const item = requestQueue[i];
    if (!item?.key) continue;

    const itemKeyNorm = normalizeKey(item.key) ?? item.key;

    if (itemKeyNorm === from || item.key === fromKey) {
      item.key = to;
    }
  }
};

type QueueStrategy = 'append' | 'replace' | 'purge-then-append';

type QueueItem = {
  request: (originalEvent?: any) => Promise<void>;
  requestInfo: EventUpdateRequest;
  onLimitReached?: () => void;
  key?: string;
  strategy: QueueStrategy;
};

const requestQueue: QueueItem[] = [];
let isProcessing = false;
let isRequestUpdateRequired = false;

const findLastIndexByKey = (key: string) => {
  const k = normalizeKey(key) ?? key;
  for (let i = requestQueue.length - 1; i >= 0; i--) {
    const itemKey = requestQueue[i]?.key;
    if (!itemKey) continue;
    if ((normalizeKey(itemKey) ?? itemKey) === k) return i;
  }
  return -1;
};

const purgeByKey = (key: string): boolean => {
  const k = normalizeKey(key) ?? key;
  let wasPurged = false;

  for (let i = requestQueue.length - 1; i >= 0; i--) {
    const itemKey = requestQueue[i]?.key;
    if (!itemKey) continue;
    if ((normalizeKey(itemKey) ?? itemKey) === k) {
      requestQueue.splice(i, 1);
      wasPurged = true;
    }
  }
  return wasPurged;
};

const processQueue = async () => {
  if (isProcessing) return;
  isProcessing = true;

  while (requestQueue.length > 0) {
    const next = requestQueue.shift();

    if (!next) continue;

    await next.request(next.requestInfo?.originalEvent);
  }

  isProcessing = false;
};

const addToQueue = (
  request: (originalEvent?: any) => Promise<void>,
  requestInfo: EventUpdateRequest,
  onLimitReached?: () => void,
  options?: {
    key?: string;
    strategy?: QueueStrategy;
  }
) => {
  const rawKey = options?.key;
  const key = normalizeKey(rawKey);
  const strategy: QueueStrategy = options?.strategy ?? 'append';

  if (key) {
    const existingIdIndex = findLastIndexByKey(key);

    if (existingIdIndex >= 0 && requestQueue[existingIdIndex].requestInfo?.originalEvent) {
      requestInfo.originalEvent = requestQueue[existingIdIndex].requestInfo.originalEvent;
    }

    if (strategy === 'purge-then-append') {
      purgeByKey(key);
    } else if (strategy === 'replace') {
      const idx = findLastIndexByKey(key);
      if (idx >= 0) {
        requestQueue[idx] = { request, requestInfo, onLimitReached, key, strategy };
      } else {
        requestQueue.push({ request, requestInfo, onLimitReached, key, strategy });
      }

      if (requestQueue.length > MAX_CALL_LIMIT && onLimitReached) onLimitReached();
      processQueue();
      return;
    }
  }

  requestQueue.push({ request, requestInfo, onLimitReached, key, strategy });

  if (requestQueue.length > MAX_CALL_LIMIT && onLimitReached) onLimitReached();

  processQueue();
};

const setRequestUpdateRequired = (value: boolean) => {
  isRequestUpdateRequired = value;
};

export {
  addToQueue,
  setRequestUpdateRequired,
  isRequestUpdateRequired,
  setQueueKeyAlias,
  purgeOnly
};
